import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BASE_URL } from '../services/api';

// Font yükleme yardımcısı (Türkçe karakter desteği için)
const getBase64FromUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("Font yüklenemedi, varsayılan font kullanılacak.", e);
    return null;
  }
};

const addTurkishFonts = async (doc) => {
  const fontRegular = await getBase64FromUrl('/fonts/Roboto-Regular.ttf');
  const fontBold = await getBase64FromUrl('/fonts/Roboto-Medium.ttf');

  if (fontRegular) {
    doc.addFileToVFS('Roboto-Regular.ttf', fontRegular);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
  }
  
  if (fontBold) {
    doc.addFileToVFS('Roboto-Medium.ttf', fontBold);
    doc.addFont('Roboto-Medium.ttf', 'Roboto', 'bold');
  }
  
  if (fontRegular || fontBold) {
      doc.setFont('Roboto');
  }
};

// Logoyu URL'den Base64 formatına çeviren yardımcı fonksiyon
const getLogo = () => {
  return new Promise((resolve) => {
    const img = new Image();
    // Cache sorununu önlemek için timestamp ekliyoruz
    img.src = `${BASE_URL}/uploads/pdf-logo.png?v=` + new Date().getTime();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      // PDF logosu yoksa ana logoyu dene
      const fallbackImg = new Image();
      fallbackImg.src = `${BASE_URL}/uploads/logo.png?v=` + new Date().getTime();
      fallbackImg.crossOrigin = 'Anonymous';
      fallbackImg.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = fallbackImg.width;
        canvas.height = fallbackImg.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(fallbackImg, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      fallbackImg.onerror = () => {
        resolve(null); // Hiçbir logo yoksa null dön
      };
    };
  });
};

export const generateOfferPDF = async (offer, bankAccounts = [], settings = {}) => {
  const doc = new jsPDF();
  await addTurkishFonts(doc); // Türkçe fontları yükle
  const logo = await getLogo();

  // Para birimi kontrolü (TL -> TRY düzeltmesi)
  const currencyCode = (offer.currency === 'TL' ? 'TRY' : offer.currency) || 'TRY';

  // Tutar Hesaplamaları
  const taxRate = offer.tax_rate || 0;
  const grandTotal = parseFloat(offer.total_amount);
  const subTotal = grandTotal / (1 + taxRate / 100);
  const taxAmount = grandTotal - subTotal;

  // Renkler
  const primaryColor = [41, 128, 185]; // Mavi
  const secondaryColor = [52, 73, 94]; // Koyu Gri
  const lightGrey = [245, 245, 245];

  // --- LOGO ---
  if (logo) {
    doc.addImage(logo, 'PNG', 14, 10, 50, 0); // Genişlik 50, yükseklik oranlı
  }

  // --- BAŞLIK (SAĞ ÜST) ---
  doc.setFontSize(24);
  doc.setTextColor(...primaryColor);
  doc.setFont("Roboto", "bold");
  doc.text("TEKLİF", 196, 25, { align: 'right' });

  // --- ŞİRKET BİLGİLERİ (SOL - LOGO ALTI) ---
  const startY = logo ? 45 : 30; // Başlangıç Y koordinatı
  let leftY = startY;
  
  doc.setFontSize(10);
  doc.setTextColor(...secondaryColor);
  
  // Firma Adı
  if (settings.company_name) {
    doc.setFont("Roboto", "bold");
    doc.text(settings.company_name, 14, leftY);
    leftY += 5;
  }
  
  doc.setFont("Roboto", "normal");
  if (settings.company_email) {
    doc.text(settings.company_email, 14, leftY);
    leftY += 5;
  }
  if (settings.company_phone) {
    doc.text(`Tel: ${settings.company_phone}`, 14, leftY);
    leftY += 5;
  }
  if (settings.company_address) {
    const splitAddress = doc.splitTextToSize(settings.company_address, 80);
    doc.text(splitAddress, 14, leftY);
    leftY += (splitAddress.length * 4); // Adres satır sayısına göre boşluk bırak
  }

  // --- TEKLİF DETAYLARI (SAĞ) ---
  const dateStr = new Date(offer.offer_date).toLocaleDateString('tr-TR');
  const validStr = offer.valid_until ? new Date(offer.valid_until).toLocaleDateString('tr-TR') : '-';
  
  let rightY = startY;
  doc.setFont("Roboto", "bold");
  doc.text("Teklif No:", 140, rightY);
  doc.text("Tarih:", 140, rightY + 5);
  doc.text("Gecerlilik:", 140, rightY + 10);

  doc.setFont("Roboto", "normal");
  // Teklif No: 1000'den başlatma (ID 1 -> 1000 olması için 999 ekliyoruz)
  doc.text(`#${999 + offer.id}`, 196, rightY, { align: 'right' });
  doc.text(dateStr, 196, rightY + 5, { align: 'right' });
  doc.text(validStr, 196, rightY + 10, { align: 'right' });
  
  rightY += 15; // Sağ tarafın yüksekliği

  // --- AYIRICI ÇİZGİ ---
  // Sol veya sağ taraftan hangisi daha aşağıdaysa oradan devam et
  const maxY = Math.max(leftY, rightY) + 5;
  
  doc.setDrawColor(200);
  doc.line(14, maxY, 196, maxY);

  // --- MÜŞTERİ BİLGİLERİ ---
  let customerY = maxY + 10;
  doc.setFontSize(11);
  doc.setTextColor(...primaryColor);
  doc.setFont("Roboto", "bold");
  doc.text("SAYIN:", 14, customerY);

  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(offer.company_name, 14, customerY + 6);
  
  doc.setFont("Roboto", "normal");
  if(offer.email) doc.text(offer.email, 14, customerY + 11);
  if(offer.address) {
    const splitAddress = doc.splitTextToSize(offer.address, 100);
    doc.text(splitAddress, 14, customerY + 16);
  }

  // --- ÜRÜN TABLOSU ---
  // Sütunlar: Ürün, Birim, Miktar, Birim Fiyat, Toplam
  const tableColumn = ["Urun / Hizmet", "Birim", "Miktar", "Birim Fiyat", "Toplam"];
  const tableRows = [];

  const unitMap = {
    'adet': 'Adet',
    'kg': 'KG',
    'cuval': 'Çuval'
  };

  offer.items.forEach(item => {
    const offerData = [
      item.product_name,
      unitMap[item.unit] || item.unit || 'Adet',
      item.quantity,
      new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currencyCode }).format(item.unit_price),
      new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currencyCode }).format(item.total_price)
    ];
    tableRows.push(offerData);
  });

  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    startY: customerY + 30,
    theme: 'grid',
    headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', font: 'Roboto' },
    styles: { fontSize: 10, cellPadding: 3, textColor: secondaryColor, font: 'Roboto' },
    alternateRowStyles: { fillColor: lightGrey },
    columnStyles: {
        0: { cellWidth: 'auto' }, // Ürün (Otomatik genişlik)
        1: { cellWidth: 20, halign: 'center' }, // Birim
        2: { cellWidth: 20, halign: 'center' }, // Miktar
        3: { cellWidth: 30, halign: 'right' }, // Birim Fiyat
        4: { cellWidth: 30, halign: 'right' }  // Toplam
    }
  });

  // --- TOPLAM TUTAR ---
  const finalY = doc.lastAutoTable.finalY + 10;
  
  // Toplam Kutusu (Arka Plan)
  doc.setFillColor(...lightGrey);
  doc.rect(130, finalY - 5, 66, 30, 'F');

  // Ara Toplam
  doc.setFontSize(10);
  doc.setTextColor(...secondaryColor);
  doc.setFont("Roboto", "normal");
  doc.text("Ara Toplam:", 135, finalY);
  doc.text(new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currencyCode }).format(subTotal), 192, finalY, { align: 'right' });

  // KDV
  doc.text(`KDV (%${taxRate}):`, 135, finalY + 6);
  doc.text(new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currencyCode }).format(taxAmount), 192, finalY + 6, { align: 'right' });

  // Genel Toplam
  doc.setFontSize(12);
  doc.setFont("Roboto", "bold");
  doc.setTextColor(...primaryColor);
  doc.text("Genel Toplam:", 135, finalY + 16);
  
  doc.setFontSize(14);
  doc.text(new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currencyCode }).format(grandTotal), 192, finalY + 16, { align: 'right' });

  let currentY = finalY + 30;

  // --- NOTLAR ---
  if (offer.notes) {
    doc.setFontSize(10);
    doc.setTextColor(...primaryColor);
    doc.setFont("Roboto", "bold");
    doc.text("Notlar:", 14, currentY);
    
    doc.setFontSize(9);
    doc.setTextColor(...secondaryColor);
    doc.setFont("Roboto", "normal");
    const splitNotes = doc.splitTextToSize(offer.notes, 180);
    doc.text(splitNotes, 14, currentY + 6);
    
    // Notların kapladığı alanı hesapla ve currentY'yi güncelle (satır sayısı * satır yüksekliği)
    currentY += 6 + (splitNotes.length * 5) + 10;
  }

  // --- BANKA HESAP BİLGİLERİ ---
  if (bankAccounts && bankAccounts.length > 0) {
    // Sayfa sonuna geldiyse yeni sayfa ekle
    if (currentY > 250) {
      doc.addPage();
      currentY = 20;
    }

    doc.setFontSize(10);
    doc.setTextColor(...primaryColor);
    doc.setFont("Roboto", "bold");
    doc.text("Banka Hesap Bilgileri:", 14, currentY);
    currentY += 6;

    doc.setFontSize(9);
    doc.setTextColor(...secondaryColor);
    doc.setFont("Roboto", "normal");

    bankAccounts.forEach(account => {
      const bankLine = `${account.bank_name} (${account.currency}) - IBAN: ${account.iban}`;
      doc.text(bankLine, 14, currentY);
      currentY += 5;
    });
  }

  // --- ALT BİLGİ (SAYFA NUMARALARI) ---
  const pageCount = doc.internal.getNumberOfPages();
  for(let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text("Bu belge bilgisayar ortaminda olusturulmustur.", 14, 285);
    doc.text(`Sayfa ${i} / ${pageCount}`, 196, 285, { align: 'right' });
  }

  // Dosyayı İndir
  doc.save(`Teklif_${offer.id}_${offer.company_name}.pdf`);
};

export const generateStatementPDF = async (account, transactions, finalBalance, carryOverBalance = 0, startDate = null, endDate = null, settings = {}) => {
  const doc = new jsPDF();
  await addTurkishFonts(doc); // Türkçe fontları yükle
  const logo = await getLogo();

  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;
  
  // Renkler
  const primaryColor = [41, 128, 185]; // Mavi
  const secondaryColor = [52, 73, 94]; // Koyu Gri
  const lightGray = [245, 245, 245];

  // --- BAŞLIK ---
  let yPos = 15;
  if (logo) {
    doc.addImage(logo, 'PNG', margin, yPos, 40, 0);
  }

  doc.setFontSize(20);
  doc.setTextColor(...primaryColor);
  doc.setFont("Roboto", "bold");
  doc.text("CARİ HESAP EKSTRESİ", pageWidth - margin, yPos + 10, { align: 'right' });

  doc.setFontSize(10);
  doc.setTextColor(...secondaryColor);
  doc.setFont("Roboto", "normal");
  
  let dateRangeStr = `Tarih: ${new Date().toLocaleDateString('tr-TR')}`;
  if (startDate || endDate) {
    const s = startDate ? new Date(startDate).toLocaleDateString('tr-TR') : 'Başlangıç';
    const e = endDate ? new Date(endDate).toLocaleDateString('tr-TR') : 'Bugün';
    dateRangeStr += `\nDönem: ${s} - ${e}`;
  }
  doc.text(dateRangeStr, pageWidth - margin, yPos + 20, { align: 'right' });

  yPos += 35;

  // --- FİRMA VE MÜŞTERİ BİLGİLERİ ---
  const colWidth = (pageWidth - (margin * 2)) / 2;
  
  // Sol: Düzenleyen (Biz)
  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text("DÜZENLEYEN:", margin, yPos);
  
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.setFont("Roboto", "bold");
  doc.text(settings.company_name || 'Firma Adı', margin, yPos + 5);
  
  doc.setFont("Roboto", "normal");
  doc.setFontSize(9);
  let leftY = yPos + 10;
  if (settings.company_address) {
    const addr = doc.splitTextToSize(settings.company_address, colWidth - 5);
    doc.text(addr, margin, leftY);
    leftY += (addr.length * 4);
  }
  if (settings.company_phone) { doc.text(`Tel: ${settings.company_phone}`, margin, leftY); leftY += 4; }
  if (settings.company_email) { doc.text(`E-posta: ${settings.company_email}`, margin, leftY); leftY += 4; }

  // Sağ: Sayın (Müşteri)
  const rightX = pageWidth / 2 + 5;
  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text("SAYIN:", rightX, yPos);

  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.setFont("Roboto", "bold");
  doc.text(account.company_name, rightX, yPos + 5);

  doc.setFont("Roboto", "normal");
  doc.setFontSize(9);
  let rightY = yPos + 10;
  if (account.address) {
    const addr = doc.splitTextToSize(account.address, colWidth - 5);
    doc.text(addr, rightX, rightY);
    rightY += (addr.length * 4);
  }
  if (account.tax_number) { doc.text(`Vergi No: ${account.tax_number}`, rightX, rightY); rightY += 4; }
  if (account.tax_office) { doc.text(`Vergi Dairesi: ${account.tax_office}`, rightX, rightY); rightY += 4; }
  if (account.authorized_person) { doc.text(`Yetkili: ${account.authorized_person}`, rightX, rightY); rightY += 4; }

  yPos = Math.max(leftY, rightY) + 10;

  // --- TABLO ---
  const tableColumn = ["Tarih", "İşlem Türü", "Açıklama", "Borç", "Alacak", "Bakiye"];
  const tableRows = [];
  
  let runningBalance = carryOverBalance;
  let totalDebt = 0;
  let totalCredit = 0;

  // --- DEVREDEN BAKİYE SATIRI ---
  if (startDate && Math.abs(carryOverBalance) > 0.005) {
    const formattedAmount = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(Math.abs(carryOverBalance));
    const balanceDir = runningBalance > 0 ? (account.account_type === 'musteri' ? '(B)' : '(A)') : (account.account_type === 'musteri' ? '(A)' : '(B)');

    tableRows.push([
      { content: new Date(startDate).toLocaleDateString('tr-TR'), styles: { fontStyle: 'bold' } },
      { content: 'DEVİR', styles: { fontStyle: 'bold' } },
      { content: 'Devreden Bakiye', styles: { fontStyle: 'bold' } },
      { content: '-', styles: { halign: 'right' } },
      { content: '-', styles: { halign: 'right' } },
      { content: `${formattedAmount} ${balanceDir}`, styles: { fontStyle: 'bold', halign: 'right' } }
    ]);
  }

  // Tarihe göre eskiden yeniye sırala
  const sortedTransactions = [...transactions].sort((a, b) => new Date(a.date) - new Date(b.date));

  sortedTransactions.forEach(item => {
    const amount = parseFloat(item.amount);
    let debtVal = 0;
    let creditVal = 0;
    let signedAmount = 0;

    // Bakiye Mantığı (CurrentAccountDetailPage ile aynı)
    if (account.account_type === 'musteri') {
      if (item.type === 'borc' || item.type === 'odeme') {
        signedAmount = amount;
        debtVal = amount;
      } else if (item.type === 'alacak' || item.type === 'tahsilat') {
        signedAmount = -amount;
        creditVal = amount;
      }
    } else {
      if (item.type === 'alacak') {
        signedAmount = amount;
        creditVal = amount;
      } else if (item.type === 'borc' || item.type === 'odeme' || item.type === 'tahsilat') {
        signedAmount = -amount;
        debtVal = amount;
      }
    }

    // Teslimat satırları bakiyeyi etkilemez ama listede görünür
    if (item.type === 'teslimat') {
      tableRows.push([
        new Date(item.date).toLocaleDateString('tr-TR'),
        'TESLİMAT',
        item.description,
        { content: '-', styles: { halign: 'right' } },
        { content: '-', styles: { halign: 'right' } },
        { content: '-', styles: { halign: 'right' } }
      ]);
      return;
    }

    runningBalance += signedAmount;
    totalDebt += debtVal;
    totalCredit += creditVal;

    const balanceDir = runningBalance > 0 ? (account.account_type === 'musteri' ? '(B)' : '(A)') : (account.account_type === 'musteri' ? '(A)' : '(B)');

    tableRows.push([
      new Date(item.date).toLocaleDateString('tr-TR'),
      item.type.toUpperCase(),
      item.description,
      { content: debtVal > 0 ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(debtVal) : '-', styles: { halign: 'right' } },
      { content: creditVal > 0 ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(creditVal) : '-', styles: { halign: 'right' } },
      { content: `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(Math.abs(runningBalance))} ${balanceDir}`, styles: { halign: 'right' } }
    ]);
  });

  // Toplam Satırı
  tableRows.push([
    { content: 'TOPLAM', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right' } },
    { content: new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(totalDebt), styles: { fontStyle: 'bold', halign: 'right' } },
    { content: new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(totalCredit), styles: { fontStyle: 'bold', halign: 'right' } },
    { content: '', styles: { halign: 'right' } }
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [tableColumn],
    body: tableRows,
    theme: 'striped',
    headStyles: { fillColor: primaryColor, textColor: 255, font: 'Roboto', fontStyle: 'bold' },
    styles: { font: 'Roboto', fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
    columnStyles: {
      0: { cellWidth: 25 }, // Tarih
      1: { cellWidth: 25 }, // İşlem Türü
      2: { cellWidth: 'auto' }, // Açıklama
      3: { cellWidth: 25, halign: 'right' }, // Borç
      4: { cellWidth: 25, halign: 'right' }, // Alacak
      5: { cellWidth: 30, halign: 'right' }  // Bakiye
    },
    didDrawPage: (data) => {
        // Sayfa Altı Bilgisi
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Sayfa ${doc.internal.getNumberOfPages()}`, data.settings.margin.left, pageHeight - 10);
        doc.text(new Date().toLocaleString('tr-TR'), pageWidth - data.settings.margin.right, pageHeight - 10, { align: 'right' });
    }
  });

  // --- ALT BİLGİ (BAKİYE) ---
  const finalY = doc.lastAutoTable.finalY + 10;
  
  // Bakiye Kutusu
  doc.setFillColor(...lightGray);
  doc.rect(pageWidth - 80 - margin, finalY, 80, 20, 'F');
  
  doc.setFont('Roboto', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...secondaryColor);
  doc.text("GENEL BAKİYE", pageWidth - 80 - margin + 5, finalY + 7);
  
  doc.setFontSize(12);
  doc.setTextColor(...primaryColor);
  const balanceFormatted = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(Math.abs(finalBalance));
  const balanceLabel = finalBalance > 0 ? (account.account_type === 'musteri' ? "(Borclu)" : "(Alacakli)") : finalBalance < 0 ? (account.account_type === 'musteri' ? "(Alacakli)" : "(Borclu)") : "(Dengede)";
  
  doc.text(`${balanceFormatted} TL ${balanceLabel}`, pageWidth - 80 - margin + 5, finalY + 15);

  doc.save(`Ekstre_${account.company_name}.pdf`);
};

export const generateDeliveryNotePDF = async (sale, delivery, settings = {}) => {
  // A4 Yatay (Landscape) - İki A5 dikey yan yana
  const doc = new jsPDF('l', 'mm', 'a4');
  await addTurkishFonts(doc);
  const logo = await getLogo();

  const pageWidth = doc.internal.pageSize.width; // 297mm
  const pageHeight = doc.internal.pageSize.height; // 210mm
  const halfWidth = pageWidth / 2; // 148.5mm
  
  // Renkler
  const primaryColor = [41, 128, 185];
  const secondaryColor = [52, 73, 94];

  // İçerik Çizme Fonksiyonu (Tekrarlanan Kod)
  const drawContent = (offsetX) => {
    const contentWidth = halfWidth;
    
    // --- LOGO ---
    if (logo) {
      doc.addImage(logo, 'PNG', 10 + offsetX, 10, 40, 0);
    }

    // --- BAŞLIK ---
    doc.setFontSize(16);
    doc.setTextColor(...primaryColor);
    doc.setFont("Roboto", "bold");
    doc.text("TESLİM FİŞİ", (contentWidth - 10) + offsetX, 20, { align: 'right' });

    // --- FİRMA BİLGİLERİ ---
    let yPos = logo ? 35 : 20;
    doc.setFontSize(9);
    doc.setTextColor(...secondaryColor);
    doc.setFont("Roboto", "bold");
    if (settings.company_name) {
      doc.text(settings.company_name, 10 + offsetX, yPos);
      yPos += 5;
    }
    doc.setFont("Roboto", "normal");
    if (settings.company_phone) {
      doc.text(`Tel: ${settings.company_phone}`, 10 + offsetX, yPos);
      yPos += 5;
    }
    
    // --- FİŞ DETAYLARI ---
    doc.setFontSize(9);
    const dateStr = delivery.date ? new Date(delivery.date).toLocaleDateString('tr-TR') : new Date().toLocaleDateString('tr-TR');
    doc.text(`Tarih: ${dateStr}`, (contentWidth - 10) + offsetX, 30, { align: 'right' });
    doc.text(`Fiş No: #${delivery.id || '-'}`, (contentWidth - 10) + offsetX, 35, { align: 'right' });
    doc.text(`Satış No: #${sale.id}`, (contentWidth - 10) + offsetX, 40, { align: 'right' });

    // --- MÜŞTERİ ---
    yPos += 5;
    doc.setDrawColor(200);
    doc.line(10 + offsetX, yPos, (contentWidth - 10) + offsetX, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(...primaryColor);
    doc.setFont("Roboto", "bold");
    doc.text("MÜŞTERİ:", 10 + offsetX, yPos);
    doc.setTextColor(0);
    doc.setFont("Roboto", "normal");
    doc.text(sale.company_name || '', 30 + offsetX, yPos);

    yPos += 10;

    // --- TABLO ---
    const tableColumn = ["Ürün / Açıklama", "Miktar"];
    const tableRows = [];

    // Satıştaki ürünleri özetle
    let items = [];
    try {
      if (Array.isArray(sale.items)) items = sale.items;
      else if (typeof sale.items === 'string') items = JSON.parse(sale.items);
      if (typeof items === 'string') items = JSON.parse(items);
    } catch (e) { items = []; }

    const productNames = items.map(i => i.product_name).join(', ');
    const unit = items.length > 0 ? (items[0].unit || 'Adet') : 'Adet';

    tableRows.push([
      productNames || 'Satış Teslimatı',
      `${delivery.quantity} ${unit}`
    ]);

    const leftMargin = 10 + offsetX;
    const rightMargin = pageWidth - (offsetX + contentWidth) + 10;

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: primaryColor, textColor: 255, font: 'Roboto' },
      styles: { fontSize: 10, cellPadding: 3, font: 'Roboto' },
      margin: { left: leftMargin, right: rightMargin }
    });

    // --- İMZA ---
    const finalY = doc.lastAutoTable.finalY + 30;
    
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.setFont("Roboto", "bold");

    doc.text("Teslim Eden", 30 + offsetX, finalY, { align: 'center' });
    doc.text("Teslim Alan", (contentWidth - 30) + offsetX, finalY, { align: 'center' });

    doc.setFont("Roboto", "normal");
    doc.setFontSize(8);
    doc.text(delivery.delivered_by || "Yetkili", 30 + offsetX, finalY + 5, { align: 'center' });
    doc.text((sale.company_name || '').substring(0, 20), (contentWidth - 30) + offsetX, finalY + 5, { align: 'center' });

    doc.text(".............................", 30 + offsetX, finalY + 15, { align: 'center' });
    doc.text(".............................", (contentWidth - 30) + offsetX, finalY + 15, { align: 'center' });
  };

  // Sol Kopya
  drawContent(0);

  // Sağ Kopya
  drawContent(halfWidth);

  // Kesme Çizgisi
  doc.setDrawColor(150);
  doc.setLineDash([3, 3], 0);
  doc.line(halfWidth, 10, halfWidth, pageHeight - 10);
  
  // Makas İkonu
  doc.setFontSize(12);
  doc.setTextColor(100);
  doc.text("✂", halfWidth, 8, { align: 'center' });
  doc.text("✂", halfWidth, pageHeight - 5, { align: 'center' });

  doc.save(`Teslim_Fisi_${sale.id}_${delivery.id || 'new'}.pdf`);
};
